import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface BatchLine {
  id: number;
  transaction_type: string;
  source_type: string;
  source_id: number;
  source_event_at: string;
  amount: string;
  debit_chart_of_account: { code: string; name: string };
  credit_chart_of_account: { code: string; name: string };
  status: string;
  rejection_reason: string | null;
  requeued_at: string | null;
}

interface BatchDetail {
  id: number;
  batch_number: string;
  batch_date: string;
  status: string;
  total_debit: string;
  total_credit: string;
  notes: string | null;
  lines: BatchLine[];
}

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  contribution_employer_portion: 'Employer Contributions',
  contribution_employee_portion: 'Employee Contributions',
  contribution_interest: 'Contribution Interest',
  penalty_income: 'Penalty & Surcharge Income',
  benefit_withdrawal_disbursement: 'Withdrawal Benefit Disbursement',
  benefit_death_disbursement: 'Death Benefit Disbursement',
  benefit_return_to_suspense: 'Benefit Returned to Suspense',
  benefit_reissue_from_suspense: 'Benefit Reissued from Suspense',
};

const LINE_STATUS_BADGE: Record<string, string> = {
  pending: 'badge-scheduled',
  posted: 'badge-active',
  rejected: 'badge-urgent',
};

export function GlBatchDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();

  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [rejecting, setRejecting] = useState<BatchLine | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const canReconcile = can('gl_interface.reconcile');
  const canExport = can('gl_interface.export');
  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  const load = () => api.get<BatchDetail>(`/gl-batches/${id}`).then((res) => setBatch(res.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const exportBatch = () => {
    if (!batch) return;
    downloadFile(api, `/gl-batches/${batch.id}/export`, `${batch.batch_number}.csv`).then(load);
  };

  const markPosted = async () => {
    if (!batch) return;
    if (!(await confirmAction('Mark every remaining pending line as posted once FAMS has confirmed acceptance?', { confirmLabel: 'Mark Posted' }))) return;
    try {
      await api.post(`/gl-batches/${batch.id}/mark-posted`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const submitReject = async (e: FormEvent) => {
    e.preventDefault();
    if (!rejecting) return;
    try {
      await api.post(`/gl-batch-lines/${rejecting.id}/reject`, { rejection_reason: rejectionReason });
      setRejecting(null);
      setRejectionReason('');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Reject failed.');
    }
  };

  const requeue = async (line: BatchLine) => {
    try {
      await api.post(`/gl-batch-lines/${line.id}/requeue`);
      showSuccess('Line requeued — it will be re-extracted into a new batch on the next run.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  if (!batch) return null;

  const hasPending = batch.lines.some((l) => l.status === 'pending');

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/gl/batches')} />
          <h1>{batch.batch_number}</h1>
        </div>
        <div className="page-header-actions">
          <span className="badge badge-neutral">{batch.status}</span>
          {canExport && batch.status === 'generated' && (
            <IconButton icon={IconDownload} label="Export CSV" onClick={exportBatch} />
          )}
          {canReconcile && batch.status === 'exported' && hasPending && (
            <button type="button" className="btn btn-primary" onClick={markPosted}>
              Mark Posted
            </button>
          )}
        </div>
      </header>

      {batch.status === 'failed' && batch.notes && (
        <div className="card">
          <p className="alert alert-error">{batch.notes}</p>
        </div>
      )}

      <div className="card">
        <p>
          <strong>Batch date:</strong> {batch.batch_date} &nbsp;|&nbsp; <strong>Total:</strong> {fmtMoney(batch.total_debit)}
        </p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Transaction Type</th>
                <th>Source</th>
                <th>Amount</th>
                <th>Debit Account</th>
                <th>Credit Account</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {batch.lines.map((line) => (
                <tr key={line.id}>
                  <td className="muted">{TRANSACTION_TYPE_LABELS[line.transaction_type] ?? line.transaction_type}</td>
                  <td className="muted">
                    {line.source_type.split('\\').pop()} #{line.source_id}
                  </td>
                  <td>{fmtMoney(line.amount)}</td>
                  <td className="muted">
                    {line.debit_chart_of_account.code} — {line.debit_chart_of_account.name}
                  </td>
                  <td className="muted">
                    {line.credit_chart_of_account.code} — {line.credit_chart_of_account.name}
                  </td>
                  <td>
                    <span className={`badge ${LINE_STATUS_BADGE[line.status] ?? 'badge-neutral'}`}>{line.status}</span>
                    {line.status === 'rejected' && line.rejection_reason && (
                      <div className="muted" style={{ fontSize: '0.85em' }}>
                        {line.rejection_reason}
                        {line.requeued_at && ' (requeued)'}
                      </div>
                    )}
                  </td>
                  <td className="right">
                    {canReconcile && line.status === 'pending' && batch.status === 'exported' && (
                      <button type="button" className="btn btn-danger-ghost" onClick={() => setRejecting(line)}>
                        Reject
                      </button>
                    )}
                    {canReconcile && line.status === 'rejected' && !line.requeued_at && (
                      <button type="button" className="btn btn-ghost" onClick={() => requeue(line)}>
                        Requeue
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {batch.lines.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted center">
                    No lines in this batch.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rejecting && (
        <Modal title={`Reject line — ${TRANSACTION_TYPE_LABELS[rejecting.transaction_type] ?? rejecting.transaction_type}`} onClose={() => setRejecting(null)}>
          <form onSubmit={submitReject} className="form-grid">
            <p className="muted span-2">Stands in for FAMS bouncing this specific transaction. It will not be re-extracted until requeued.</p>
            <label className="span-2">
              Reason *
              <textarea rows={3} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} required />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setRejecting(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Reject</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
